import React, { FC } from 'react';
import classNames from 'classnames';
import { RoleAction, RoleId } from '@wix/members-domain-ts';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import { ActionsMenuLayout } from 'wix-ui-tpa';

import { AdditionalAction, DataHook, Experiments } from '../../../types';
import roleIcons from '../../../constants/role-icons';
import RoleActionIcon from './RoleActionIcon';
import RoleActionLink, { RoleActionLinkClickHandler } from './RoleActionLink';
import RoleActionLinkText from './RenderActionLinkText';
import PopoverContentSeparator from './PopoverContentSeparator';
import styles from './PopoverContent.scss';

export type OnRoleAction = (roleId: RoleId) => void;

export type OnAdditionalAction = (id: AdditionalAction['id']) => void;

interface BaseProps {
  onRoleAction: OnRoleAction;
  onAdditionalAction: OnAdditionalAction;
}

interface RenderRoleActionProps extends Omit<BaseProps, 'onAdditionalAction'> {
  action: string;
  roleId: RoleId;
}

interface AdditionalActionProps extends Omit<BaseProps, 'onRoleAction'> {
  id: AdditionalAction['id'];
  action: AdditionalAction['action'];
}

export interface PopoverContentProps extends BaseProps {
  additionalActions: AdditionalAction[];
  rolesActions: RoleAction[];
  isMobile: boolean;
}

const DIVIDER = 'divider';

const RoleActionItem: FC<RenderRoleActionProps> = ({
  roleId,
  action,
  onRoleAction,
}) => {
  const { t } = useTranslation();

  const onClick: RoleActionLinkClickHandler = (event) => {
    event.preventDefault();
    onRoleAction(roleId);
  };

  return (
    <RoleActionLink
      key={action}
      dataHook={roleId}
      isMenuItem={true}
      onClick={onClick}
    >
      <RoleActionIcon t={t}>{roleId}</RoleActionIcon>
      <RoleActionLinkText>{t(action)}</RoleActionLinkText>
    </RoleActionLink>
  );
};

const AdditionalActionItem: FC<AdditionalActionProps> = ({
  id,
  action,
  onAdditionalAction,
}) => {
  const { t } = useTranslation();

  const onClick: RoleActionLinkClickHandler = (event) => {
    event.preventDefault();
    onAdditionalAction(id);
  };

  return (
    <RoleActionLink key={id} dataHook={id} isMenuItem={true} onClick={onClick}>
      <RoleActionLinkText>{t(action)}</RoleActionLinkText>
    </RoleActionLink>
  );
};

const shouldRenderSeparator = (
  actionsWithIcon: RoleAction[],
  actionsWithoutIcon: (RoleAction | AdditionalAction)[],
) => {
  const hasActionsWithIcons = actionsWithIcon.length > 0;
  const hasActionsWithoutIcons = actionsWithoutIcon.length > 0;
  return hasActionsWithIcons && hasActionsWithoutIcons;
};

const PopoverContent: FC<PopoverContentProps> = ({
  additionalActions,
  rolesActions,
  isMobile,
  onAdditionalAction,
  onRoleAction,
}) => {
  const { experiments } = useExperiments();

  const isPopoverAndTooltipMigrationEnabled = experiments.enabled(
    Experiments.PopoverAndTooltipMigration,
  );

  const roleIconsKeys = Object.keys(roleIcons);
  const withIcons = rolesActions.filter(({ roleId }) =>
    roleIconsKeys.includes(roleId),
  );
  let withoutIcons = rolesActions.filter(
    ({ roleId }) => !roleIconsKeys.includes(roleId),
  );
  const isSeparatorNeeded = shouldRenderSeparator(withIcons, [
    ...withoutIcons,
    ...additionalActions,
  ]);

  // Render "Manage badges" at the end if it's there
  const manageBadgesAction = withoutIcons.find(
    (r) => r.roleId === RoleId.MANAGE_BADGES,
  );
  if (manageBadgesAction) {
    withoutIcons = withoutIcons.filter((r) => r !== manageBadgesAction);
    withoutIcons.push(manageBadgesAction);
  }

  const rolesActionsWithIcons = withIcons.map((r) => (
    <RoleActionItem
      key={r.action}
      roleId={r.roleId}
      action={r.action}
      onRoleAction={onRoleAction}
    />
  ));
  const rolesActionsWithoutIcons = withoutIcons.map((r) => (
    <RoleActionItem
      key={r.action}
      roleId={r.roleId}
      action={r.action}
      onRoleAction={onRoleAction}
    />
  ));
  const additionalActionsWithoutIcons = additionalActions.map(
    ({ id, action }) => (
      <AdditionalActionItem
        key={id}
        id={id}
        action={action}
        onAdditionalAction={onAdditionalAction}
      />
    ),
  );

  // Add separator before "Manage badges" if there are more roles without icons
  if (manageBadgesAction && rolesActionsWithoutIcons.length > 1) {
    rolesActionsWithoutIcons.splice(
      rolesActionsWithoutIcons.length - 1,
      0,
      <PopoverContentSeparator key="separator-1" />,
    );
  }

  const allItems = [
    ...rolesActionsWithIcons,
    isSeparatorNeeded && <PopoverContentSeparator key="separator-2" />,
    ...additionalActionsWithoutIcons,
    ...rolesActionsWithoutIcons,
  ];

  const menuItemsClasses = {
    [styles.actionsContainer]: true,
    [styles.mobile]: isMobile,
    [styles.actionsContainerManyItems]:
      allItems.length > 1 && !isPopoverAndTooltipMigrationEnabled,
    [styles.actionsContainerOneItem]:
      allItems.length > 1 && !isPopoverAndTooltipMigrationEnabled,
  };

  return <div className={classNames(menuItemsClasses)}>{allItems}</div>;
};

export const MigratedPopoverContent: FC<PopoverContentProps> = ({
  additionalActions,
  rolesActions,
  onRoleAction,
  onAdditionalAction,
}) => {
  const { t } = useTranslation();

  const roleIconsKeys = Object.keys(roleIcons);
  const withIcons = rolesActions.filter(({ roleId }) =>
    roleIconsKeys.includes(roleId),
  );
  let withoutIcons = rolesActions.filter(
    ({ roleId }) => !roleIconsKeys.includes(roleId),
  );
  const isSeparatorNeeded = shouldRenderSeparator(withIcons, [
    ...withoutIcons,
    ...additionalActions,
  ]);

  // Render "Manage badges" at the end if it's there
  const manageBadgesAction = withoutIcons.find(
    (r) => r.roleId === RoleId.MANAGE_BADGES,
  );
  if (manageBadgesAction) {
    // For a divider to be visible before "Manage badges" and for keys to work
    withoutIcons.push({
      action: DIVIDER,
      roleId: RoleId.MANAGE_BADGES,
      description: '',
    });
    withoutIcons = withoutIcons.filter((r) => r !== manageBadgesAction);
    withoutIcons.push(manageBadgesAction);
  }

  return (
    <ActionsMenuLayout
      className={styles.actionsMenuLayout}
      data-hook={DataHook.Popover}
    >
      {withIcons.map(({ action, roleId }) => {
        const RoleIcon = roleIcons[roleId];

        return (
          <ActionsMenuLayout.Item
            content={t(action)}
            onClick={() => onRoleAction(roleId)}
            key={action}
            prefixIcon={<RoleIcon className={styles.migratedIconFill} />}
          />
        );
      })}

      {/* actionsMenuLayout expects child to have type (child.type === ...), to avoid error <></> is returned */}
      {isSeparatorNeeded ? <ActionsMenuLayout.Divider /> : <></>}

      {additionalActions.map(({ id, action }) => (
        <ActionsMenuLayout.Item
          content={t(action)}
          onClick={() => onAdditionalAction(id)}
          key={id}
        />
      ))}

      {withoutIcons.map(({ action, roleId }) => {
        if (action === DIVIDER) {
          return <ActionsMenuLayout.Divider />;
        }

        return (
          <ActionsMenuLayout.Item
            content={t(action)}
            onClick={() => onRoleAction(roleId)}
            key={action}
          />
        );
      })}
    </ActionsMenuLayout>
  );
};

export default PopoverContent;
